import React from "react";
import PropTypes from "prop-types";
import { Dialog } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

import SoftBox from "../SoftBox";
import SoftButton from "../SoftButton";
import SoftTypography from "../SoftTypography";
import { toDisplayText } from "../../helpers/errorText";

const NO_URL_ASSIGNED_LABEL = "No URL assigned yet";

const formatUrlLabel = (url) => {
  if (!url) return "";
  try {
    const parsed = new URL(url);
    const path =
      parsed.pathname === "/" ? "" : parsed.pathname;
    const label = `${parsed.hostname}${path}${parsed.search ? "…" : ""}`;
    return label.length > 72 ? `${label.slice(0, 72)}…` : label;
  } catch {
    return url.length > 72 ? `${url.slice(0, 72)}…` : url;
  }
};

const HubLinkSuccessDialog = ({ open, onClose, linkTitle, linkUrl }) => {
  const urlLabel = formatUrlLabel(linkUrl);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          backgroundColor: "#fff",
          border: "1px solid #EAECF0",
          boxShadow:
            "0px 8px 8px rgba(16, 24, 40, 0.04), 0px 20px 24px rgba(16, 24, 40, 0.1)",
          overflow: "hidden",
        },
      }}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(16, 24, 40, 0.4)",
        },
      }}
    >
      <SoftBox sx={{ px: 3, pt: 3, pb: 2.5, textAlign: "center" }}>
        <SoftBox
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            backgroundColor: "#ECFDF3",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2,
          }}
        >
          <CheckCircleRoundedIcon sx={{ fontSize: 32, color: "#12B76A" }} />
        </SoftBox>

        <SoftTypography
          variant="h5"
          sx={{
            color: "#101828",
            fontWeight: 600,
            fontSize: "18px",
            mb: 0.75,
          }}
        >
          Power link added
        </SoftTypography>
        <SoftTypography
          variant="body2"
          sx={{ color: "#667085", fontSize: "14px", mb: 2.5 }}
        >
          Your link is ready to use from the Hub.
        </SoftTypography>

        <SoftBox
          sx={{
            textAlign: "left",
            borderRadius: "12px",
            border: "1px solid #EAECF0",
            backgroundColor: "#F9FAFB",
            p: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
          }}
        >
          <SoftBox>
            <SoftTypography
              variant="caption"
              sx={{
                color: "#667085",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                fontSize: "11px",
                display: "block",
                mb: 0.25,
              }}
            >
              Name
            </SoftTypography>
            <SoftTypography
              variant="body2"
              sx={{ color: "#101828", fontWeight: 500, fontSize: "14px" }}
            >
              {toDisplayText(linkTitle, "—")}
            </SoftTypography>
          </SoftBox>
          <SoftBox>
            <SoftTypography
              variant="caption"
              sx={{
                color: "#667085",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                fontSize: "11px",
                display: "block",
                mb: 0.25,
              }}
            >
              URL
            </SoftTypography>
            {linkUrl ? (
              <SoftTypography
                component="a"
                href={linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                title={linkUrl}
                sx={{
                  color: "primary.main",
                  fontSize: "13px",
                  fontWeight: 500,
                  textDecoration: "none",
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {urlLabel}
              </SoftTypography>
            ) : (
              <SoftTypography
                variant="body2"
                sx={{
                  color: "#B54708",
                  fontSize: "13px",
                  fontWeight: 600,
                }}
              >
                {NO_URL_ASSIGNED_LABEL}
              </SoftTypography>
            )}
          </SoftBox>
        </SoftBox>
      </SoftBox>

      <SoftBox
        sx={{
          px: 3,
          pb: 3,
          pt: 0.5,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <SoftButton
          color="primary"
          variant="gradient"
          onClick={onClose}
          sx={{
            textTransform: "none",
            color: "#fff",
            minWidth: 120,
            borderRadius: "10px",
          }}
        >
          Done
        </SoftButton>
      </SoftBox>
    </Dialog>
  );
};

HubLinkSuccessDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  linkTitle: PropTypes.string,
  linkUrl: PropTypes.string,
};

HubLinkSuccessDialog.defaultProps = {
  linkTitle: "",
  linkUrl: "",
};

export default HubLinkSuccessDialog;
